<script lang="ts" setup>
import { watch, ref } from "vue";
import { getHomesMeetingNetHeatRequirement } from "@/services/api/chart.api";
import HouseWidget from "@/components/general/HouseWidget.vue";
import { useToast } from "vue-toastification";

const toaster = useToast();

const props = defineProps({
  token: String,
  refDate: Date,
  compact: {
    type: Boolean,
    default: false,
  },
});

const nhSummary = ref({
  counted: 0,
  colorClass: "color-grey",
  percentage: 0,
  total: 0,
});
const isLoading = ref(false);
const isLoaded = ref(false);

watch(
  () => props.token,
  () => {
    fetchData();
  },
  { immediate: true }
);

async function fetchData() {
  //console.log("NetHeatRequirement fetchData ", this.token);
  isLoaded.value = false;
  if (props.token) {
    isLoading.value = true;
    const response = await getHomesMeetingNetHeatRequirement(props.token);
    if (response) {
      nhSummary.value.counted = response.data.counted;
      nhSummary.value.total = response.data.total;
      const pct = Math.trunc((100 * response.data.meetingNetHeatRequirement) / response.data.total + 0.5);

      if (Number.isNaN(pct)) {
        nhSummary.value.colorClass = "color-grey";
      } else if (pct < 50) {
        nhSummary.value.colorClass = "color-red";
      } else if (pct < 85) {
        nhSummary.value.colorClass = "color-orange";
      } else {
        nhSummary.value.colorClass = "color-green";
      }
      nhSummary.value.percentage = pct;
      isLoaded.value = true;
    } else {
      console.error("Failed to get Net Heat Requirement summary");
      toaster.error("Ophalen van de Netto Warmtebehoefte is mislukt!");
    }
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="app-paris-proof-sidebar home" :class="{ 'app-paris-proof-sidebar--compact': props.compact }">
    <header>
      <h2>Netto warmtebehoefte</h2>
      <div class="widget-actions">
        <information-dropdown>
          <template v-slot:header>
            <h2>Netto warmtebehoefte</h2>
          </template>

          <p>Dit toont het percentage van de woningen waarvan de netto warmtebehoefte voldoet aan de standaard.</p>

          <p>
            De <b>Netto warmtevraag/behoefte</b> is een getal dat vertelt hoeveel energie er nodig is voor het verwarmen van de woning en wordt uitgedrukt in kilowattuur per vierkante meter per jaar
            (kWh/m²/jaar).
          </p>

          <p>
            De <b>Standaard</b> richt zich op de definitie van een zogenaamde “spijtvrije” verbouwing waarmee de gebouweigenaar ervan verzekerd is dat later, bij aansluiting op duurzame bronnen met
            een lagere temperatuur, niet nogmaals een ingrijpende aanpassing aan dezelfde bouwdelen noodzakelijk is.
          </p>
        </information-dropdown>
      </div>
    </header>

    <div v-if="props.compact" class="compact-layout">
      <div class="compact-layout__visual">
        <house-widget :colorClass="nhSummary.colorClass"></house-widget>
      </div>
      <div class="compact-layout__content">
        <div class="paris-score">
          <span v-if="isLoaded" :class="nhSummary.colorClass">Percentage van het aantal woningen dat voldoet aan de warmtebehoefte: {{ nhSummary.percentage }}%</span>
        </div>

        <h5 v-if="isLoaded">Gebaseerd op {{ nhSummary.counted }} van de {{ nhSummary.total }} woningen</h5>

        <div class="paris-proof-legend">
          <div class="item"><span class="color color-red"></span> <b>0 - 49%</b></div>
          <div class="item"><span class="color color-orange"></span> <b>50 - 84%</b></div>
          <div class="item"><span class="color color-green"></span> <b>85 - 100%</b></div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="paris-score">
        <span v-if="isLoaded" :class="nhSummary.colorClass"> Percentage van het aantal woningen dat voldoet aan de warmtebehoefte: {{ nhSummary.percentage }}% </span>
      </div>

      <h5 v-if="isLoaded">Gebaseerd op {{ nhSummary.counted }} van de {{ nhSummary.total }} woningen</h5>

      <v-container>
        <v-row>
          <v-col md="12" sm="6">
            <div class="paris-proof-score">
              <house-widget :colorClass="nhSummary.colorClass"></house-widget>
            </div>
          </v-col>
          <v-col md="12" sm="6">
            <div class="paris-proof-legend">
              <div class="item"><span class="color color-red"></span> <b>0 - 49%</b></div>
              <div class="item"><span class="color color-orange"></span> <b>50 - 84%</b></div>
              <div class="item"><span class="color color-green"></span> <b>85 - 100%</b></div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.app-paris-proof-sidebar--compact {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.compact-layout {
  display: flex;
  gap: 12px;
  align-items: center;
  height: 100%;
  min-height: 0;
  flex: 1;
}

.compact-layout__visual {
  flex: 0 0 44%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.compact-layout__visual :deep(svg) {
  width: 100%;
  max-width: 200px;
  max-height: 220px;
  min-width: 0;
}

.compact-layout__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.app-paris-proof-sidebar--compact .paris-score {
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.app-paris-proof-sidebar--compact h5 {
  margin-bottom: 10px;
}

.app-paris-proof-sidebar--compact .paris-proof-legend {
  margin-top: 0;
}

.app-paris-proof-sidebar--compact .paris-proof-legend .item {
  margin-top: 8px;
}
</style>
